import {
  DEFAULT_DATE_FORMAT_FNS,
  GetGroupSubjectsResponse,
  GetMessageResponse,
  GroupSubject,
  Individual,
  Recipient,
  SimpleListResult,
  useGetSchoolPropertiesQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { useSubjects } from '@schooly/hooks/use-subjects';
import { format } from 'date-fns';
import { useMemo } from 'react';

import { SchoolUserRole } from '../../constants/school';
import { MessageCriteriaItem } from '../../context/messages/MessageContext';
import { getMessagePublishDate } from '../../helpers/messages';
import { convertApiTimestamp } from '../../utils/convertApiTimestamp';
import {
  convertIndividualToUserSearchResult,
  convertMessageCriteriaToItems,
  getParentsFromIndividuals,
  getParentsOfStudentsCriteria,
  getParentsOfStudentsFromIndividuals,
  getStaffCriteria,
  getStaffFromIndividuals,
  getStaffOfStudentsFromIndividuals,
} from './helpers';

export type MessageRecipientsDerivation = {
  groupSubjects: GroupSubject[];
  subjectsData: GetGroupSubjectsResponse | undefined;
  studentProperties: ReturnType<typeof useGetSchoolPropertiesQuery>['data'];
  staffIndividuals: Individual[];
  studentIndividuals: Individual[];
  staff: SimpleListResult[];
  parents: SimpleListResult[];
  staffByIndividualStudent: SimpleListResult[];
  parentsByIndividualStudent: SimpleListResult[];
  actualParents: Recipient[];
  actualStaff: Recipient[];
  parentsCriteria: MessageCriteriaItem[];
  staffCriteria: MessageCriteriaItem[];
  criteriaDate?: string;
  publishDate?: Date;
};

export const useMessageRecipientsDerivation = (
  messageData: GetMessageResponse | undefined,
): MessageRecipientsDerivation => {
  const { schoolId = '' } = useAuth();

  const { subjects: groupSubjects, data: subjectsData } = useSubjects(
    { schoolId },
    { refetchOnMount: 'always' },
  );

  const { data: studentProperties } = useGetSchoolPropertiesQuery({
    userType: SchoolUserRole.Student,
    schoolId,
  });
  const { data: staffProperties } = useGetSchoolPropertiesQuery({
    userType: SchoolUserRole.Staff,
    schoolId,
  });

  const properties = useMemo(() => {
    const allProps = [
      ...(studentProperties?.school_properties ?? []),
      ...(staffProperties?.school_properties ?? []),
    ];

    return allProps.filter((item, index) => {
      const currIndex = allProps.findIndex((i) => i.id === item.id);
      return currIndex === index;
    });
  }, [staffProperties?.school_properties, studentProperties?.school_properties]);

  const { ageGroups: staffAgeGroups, schoolLevels: staffSchoolLevels } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Staff,
  });
  const { ageGroups: studentAgeGroups, schoolLevels: studentSchoolLevels } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });
  const { customFields = [] } = useCustomFields();

  const {
    staffIndividuals,
    studentIndividuals,
    staff,
    actualParents,
    actualStaff,
    parents,
    staffByIndividualStudent,
    parentsByIndividualStudent,
  } = useMemo(() => {
    const staffIndividuals = messageData
      ? (getStaffFromIndividuals(messageData.message_individuals) ?? [])
      : [];
    const staff = staffIndividuals.map(convertIndividualToUserSearchResult);

    const studentIndividuals = messageData
      ? (getParentsOfStudentsFromIndividuals(messageData.message_individuals) ?? [])
      : [];
    const parentsByIndividualStudent = studentIndividuals.map(convertIndividualToUserSearchResult);

    const actualParents =
      messageData?.message_recipients.filter(
        (recipient) => recipient.school_user_relation_role === SchoolUserRole.Parent,
      ) ?? [];

    const actualStaff =
      messageData?.message_recipients.filter(
        (recipient) => recipient.school_user_relation_role === SchoolUserRole.Staff,
      ) ?? [];

    // Individual parent recipients are deprecated
    //This logic should be removed as soon as there are no messages with such recipients
    const parents =
      getParentsFromIndividuals(messageData?.message_individuals)?.map(
        convertIndividualToUserSearchResult,
      ) ?? [];

    // Staff by individual student recipients are deprecated
    //This logic should be removed as soon as there are no messages with such recipients
    const staffByIndividualStudent =
      getStaffOfStudentsFromIndividuals(messageData?.message_individuals)?.map(
        convertIndividualToUserSearchResult,
      ) ?? [];

    return {
      staffIndividuals,
      studentIndividuals,
      staff,
      actualParents,
      actualStaff,
      parents,
      staffByIndividualStudent,
      parentsByIndividualStudent,
    };
  }, [messageData]);

  const { parentsMessageCriteria, staffMessageCriteria } = useMemo(() => {
    const parentsMessageCriteria =
      getParentsOfStudentsCriteria(messageData?.message_criteria) ?? [];
    const staffMessageCriteria = getStaffCriteria(messageData?.message_criteria) ?? [];

    return { parentsMessageCriteria, staffMessageCriteria };
  }, [messageData?.message_criteria]);

  const parentsCriteria = useMemo(
    () =>
      convertMessageCriteriaToItems({
        criteria: parentsMessageCriteria,
        properties,
        subjects: groupSubjects,
        ageGroups: studentAgeGroups,
        schoolLevels: studentSchoolLevels,
        customFields,
      }),
    [
      parentsMessageCriteria,
      properties,
      groupSubjects,
      studentAgeGroups,
      studentSchoolLevels,
      customFields,
    ],
  );

  const staffCriteria = useMemo(
    () =>
      convertMessageCriteriaToItems({
        criteria: staffMessageCriteria,
        properties,
        subjects: groupSubjects,
        ageGroups: staffAgeGroups,
        schoolLevels: staffSchoolLevels,
        customFields,
      }),
    [
      staffMessageCriteria,
      properties,
      groupSubjects,
      staffAgeGroups,
      staffSchoolLevels,
      customFields,
    ],
  );

  const { publishDate, criteriaDate } = useMemo(() => {
    const criteriaDateTimestamp = convertApiTimestamp(messageData?.message.criteria_date);
    const criteriaDate = criteriaDateTimestamp
      ? format(criteriaDateTimestamp, DEFAULT_DATE_FORMAT_FNS)
      : undefined;

    const publishDate = getMessagePublishDate(messageData?.message);

    return { publishDate, criteriaDate };
  }, [messageData?.message]);

  return {
    groupSubjects,
    subjectsData,
    studentProperties,
    staffIndividuals,
    studentIndividuals,
    staff,
    actualParents,
    actualStaff,
    parents,
    staffByIndividualStudent,
    parentsByIndividualStudent,
    parentsCriteria,
    staffCriteria,
    criteriaDate,
    publishDate,
  };
};
